import ListItem from '@ahamove/web-components/lib/ListItem';
import ListItemButton from '@ahamove/web-components/lib/ListItemButton';
import ListItemIcon from '@ahamove/web-components/lib/ListItemIcon';
import ListItemText from '@ahamove/web-components/lib/ListItemText';
import SvgIcon from '@ahamove/web-components/lib/SvgIcon';
import Tooltip, { tooltipClasses } from '@ahamove/web-components/lib/Tooltip';
import Typography from '@ahamove/web-components/lib/Typography';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import type { Address, AutocompleteParams, Feature } from '@/api/place/types';
import { getAutocompleteFn } from '@/api/place/useGetAutocomplete';
import { useGetHubs } from '@/api/place/useGetHubs';
import { useCurrentLocationStore } from '@/global-stores/useCurrentLocationStore';
import { useLocationStore } from '@/global-stores/useLocationStore';
import { useRentTypeStore } from '@/global-stores/useRentTypeStore';
import debounce from '@/lib/debounce';
import { PinIcon } from '@/lib/icons';
import { isServer } from '@/lib/isServer';
import { featureToAddress } from '@/lib/utils';
import { homeConfig } from '@/modules/home/homeConfig';
import DialogPickLocation from '@/shared-components/Dialog/DialogPickLocation';
import useIsMobile from '@/shared-hooks/useIsMobile';
import { useNewBooking } from '../NewBookingProvider';

type Props = {
  handleCloseTooltip: () => void;
  tooltipError: boolean;
};

export default function Location({
  handleCloseTooltip,
  tooltipError = false,
}: Props) {
  const isMobile = useIsMobile();
  const { address: defaultAddressValue, setAddress } = useLocationStore();
  const { coords: currentCoords, currentAddress } = useCurrentLocationStore();
  const { rentType } = useRentTypeStore();

  const { t } = useTranslation(homeConfig.i18nNamespaces);

  const {
    errors,
    loadingServiceModels,
    serviceModels,
    setOpenNoService,
    openNoService,
    openDialog,
    setOpenDialog,
  } = useNewBooking();
  const requiredAddress = 'location' in errors;

  const handleOpenDialogPickLocation = () => {
    setOpenDialog(true);
  };

  const handleCloseDialogPickLocation = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    if (!isServer) {
      document.body.style.setProperty(
        'overflow',
        openDialog ? 'hidden' : 'auto',
        'important'
      );
    }
  }, [openDialog]);

  const [addressOptions, setAddressOptions] = useState<readonly Address[]>([]);
  const [addressInputValue, setAddressInputValue] = useState(
    defaultAddressValue?.address
  );

  const { data: listHubs } = useGetHubs(
    {
      lng: currentCoords?.lat as number,
      lat: currentCoords?.lng as number,
    },
    { enabled: false },
    rentType
  );

  // Update inner value when props value change
  useEffect(() => {
    setAddressInputValue(defaultAddressValue?.address);
    setAddressOptions(defaultAddressValue ? [defaultAddressValue] : []);
  }, [defaultAddressValue, openDialog]);

  // Autocomplete predictions fetcher
  const fetch = React.useMemo(
    () =>
      debounce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (request: AutocompleteParams, callback: (results?: any) => void) => {
          if (currentCoords) {
            request.lat = currentCoords.lat;
            request.lng = currentCoords.lng;
          }
          getAutocompleteFn(request)
            .then((res) => {
              callback(res);
            })
            .catch(() => {
              callback({ features: [] });
            });
        },
        800
      ),
    [currentCoords]
  );

  // Runs on input change
  useEffect(() => {
    // Lock worker
    let active = true;

    // Hide options when input is empty
    // if (addressInputValue === '') {
    //   setAddressOptions((prev) => {
    //     if (
    //       addressValue &&
    //       !prev.find((o) => o.place_id === addressValue.place_id)
    //     ) {
    //       return [addressValue];
    //     }

    //     return [];
    //   });
    //   return undefined;
    // }

    if (addressInputValue === defaultAddressValue?.address) return undefined;

    if (addressInputValue === '' || addressInputValue === undefined)
      return undefined;

    if (addressOptions.findIndex((o) => o.address === addressInputValue) !== -1)
      return undefined;

    if (
      listHubs &&
      listHubs.findIndex((hub) => hub.address === addressInputValue) !== -1
    )
      return undefined;

    if (addressInputValue === currentAddress?.address) return undefined;

    // Fetch autocomplete predictions
    fetch(
      { input: addressInputValue },
      ({ features }: { features: Feature[] }) => {
        if (active) {
          let newOptions: Address[] = [];
          if (features.length > 0) {
            features.forEach((feature: Feature) => {
              const formattedAddress = featureToAddress(feature);
              formattedAddress && newOptions.push(formattedAddress);
            });
          }

          if (defaultAddressValue) {
            const fetchedAddressValueIndex = newOptions.findIndex(
              (o) => o.address === defaultAddressValue.address
            );
            // Include selected address if it is not in the predictions
            if (fetchedAddressValueIndex === -1) {
              // newOptions = [defaultAddressValue, ...newOptions];
              newOptions = [...newOptions];
            } else {
              // Place selected address at the top if it is in the predictions
              newOptions = [
                newOptions[fetchedAddressValueIndex],
                ...newOptions.slice(0, fetchedAddressValueIndex),
                ...newOptions.slice(fetchedAddressValueIndex + 1),
              ];
            }
          }

          setAddressOptions(newOptions);
        }
      }
    );

    return () => {
      // Unlock worker
      active = false;
    };
  }, [defaultAddressValue, addressInputValue, fetch]);

  const handleCloseNoService = () => {
    setOpenNoService(false);
    setAddress(null);
  };

  return (
    <>
      <ListItem
        sx={{
          py: 0,
          height: '100%',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <ListItemButton
          data-test-id="pickLocationDiv"
          disableRipple
          alignItems="flex-start"
          sx={(theme) => ({
            height: '100%',
            pr: `${theme.spacing(2)} !important`,
          })}
          onClick={() => {
            handleCloseTooltip();
            handleOpenDialogPickLocation();
          }}
        >
          <ListItemIcon sx={{ minWidth: '38px', paddingRight: 2 }}>
            <SvgIcon
              inheritViewBox
              component={PinIcon}
              sx={(theme) => ({
                path: {
                  fill: openDialog
                    ? theme.palette.info.dark
                    : theme.palette.grey[500],
                },
              })}
            />
          </ListItemIcon>
          <ListItemText
            sx={{ mb: 2 }}
            primary={
              <>
                <Typography
                  variant={isMobile ? 'h4' : 'h1'}
                  component="h2"
                  color={openDialog ? 'info.dark' : 'grey.900'}
                >
                  {t('booking:booking_box.pickup_end_return')}
                </Typography>
                <Typography
                  mt={1}
                  variant={isMobile ? 'h6' : 'h4'}
                  component="h3"
                  color={
                    requiredAddress && tooltipError
                      ? 'error.main'
                      : defaultAddressValue
                      ? 'grey.900'
                      : 'grey.500'
                  }
                  fontWeight={defaultAddressValue ? 600 : 500}
                  fontSize={isMobile ? '14px' : requiredAddress ? '20px' : 18}
                  sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: '2',
                  }}
                >
                  {defaultAddressValue ? (
                    defaultAddressValue.address
                  ) : (
                    <Tooltip
                      placement="bottom-start"
                      open={
                        Object.values(errors).length > 0 && tooltipError
                          ? true
                          : false
                      }
                      onClose={handleCloseTooltip}
                      arrow
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      title={
                        requiredAddress ? (
                          <p className="text-body14 text-neutral-10 font-medium">
                            {t('booking:location.please_enter_location_desc')}
                          </p>
                        ) : undefined
                      }
                      PopperProps={{
                        sx: {
                          [`.${tooltipClasses.tooltip}`]: {
                            bgcolor: 'error.main',
                          },
                          [`.${tooltipClasses.arrow}`]: {
                            color: 'error.main',
                          },
                        },
                      }}
                    >
                      <span>{t('booking:booking_box.select_location')}</span>
                    </Tooltip>
                  )}
                </Typography>
              </>
            }
          />
        </ListItemButton>
      </ListItem>

      <DialogPickLocation
        open={openDialog}
        onClose={handleCloseDialogPickLocation}
        onOpen={handleOpenDialogPickLocation}
        loadingServiceModels={loadingServiceModels}
        serviceModels={serviceModels}
        openNoService={openNoService}
        handleCloseNoService={handleCloseNoService}
      />
    </>
  );
}
